<template>
  <main class="home-page">
    <!-- CAROUSEL -->
    <section
      id="hero"
      :class="{ 'is-loading': !isCarouselReady }"
    >
      <VSlickCarousel
        ref="heroCarousel"
        class="carousel"
        :autoplay="true"
        :autoplay-speed="6000"
        :class="carouselColorClass"
        :arrows="false"
        :draggable="false"
        :dots="true"
        :infinite="true"
        :adaptive-height="true"
        @after-change="onCarouselAfterChange"
        @before-change="onCarouselBeforeChange"
      >
        <div
          v-for="(slide, i) of page.acf?.slides"
          :key="i"
        >
          <div
            v-if="slide.acf_fc_layout === 'split_image'"
            class="slide split-image"
            :class="[
              `img-${slide.img_side || 'right'}`,
              `style-${slide.style || 'basic'}`,
              slide.style === 'arrow-simple' ? 'style-arrow' : null,
              slide.style === 'arrow-no' ? 'style-arrow' : null
            ]"
          >
            <div class="bg">
              <div class="maybe-container">
                <div class="orange" />

                <div class="img">
                  <ul class="repeat">
                    <li
                      v-html="slide.repeating_text"
                    />
                    <li
                      v-html="slide.repeating_text"
                    />
                    <li
                      v-html="slide.repeating_text"
                    />
                    <li
                      v-html="slide.repeating_text"
                    />
                    <li
                      v-html="slide.repeating_text"
                    />
                    <li
                      v-html="slide.repeating_text"
                    />
                    <li
                      v-html="slide.repeating_text"
                    />
                  </ul>
                  <div class="aspect">
                    <img
                      v-if="slide.img && slide.img.url"
                      :src="slide.img.url"
                      :alt="slide.img.alt"
                      :fetchpriority="i === 0 ? 'high' : 'low'"
                      decoding="async"
                      :style="{
                        objectPosition: `${slide.img_x_pos}% ${slide.img_y_pos}%`,
                        objectFit: slide.img_size || 'contain',
                        display: slide.has_image
                      }"
                      @load="onImageLoad"
                    >
                    <img
                      v-if="slide.floating_image && slide.floating_image.url"
                      :src="slide.floating_image.url"
                      :alt="slide.floating_image.alt"
                      :fetchpriority="i === 0 ? 'high' : 'low'"
                      decoding="async"
                      class="float-image"
                    >
                    <div
                      v-if="slide.floating_text"
                      class="floating"
                      v-html="slide.floating_text"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div class="container">
              <div class="container-inner">
                <div
                  class="header"
                  v-html="slide.header"
                />

                <div
                  v-if="slide.img && slide.img.url"
                  class="img"
                >
                  <div class="aspect">
                    <img
                      :src="slide.img.url"
                      :alt="slide.img.alt"
                      :style="{
                        objectPosition: `${slide.img_x_pos}% ${slide.img_y_pos}%`,
                        objectFit: slide.img_size || 'contain',
                        display: slide.has_image
                      }"
                    >
                    <div
                      v-if="slide.floating_text"
                      class="floating"
                      v-html="slide.floating_text"
                    />
                  </div>
                </div>

                <div
                  class="content"
                  v-html="slide.content"
                />

                <div
                  v-if="slide.link"
                  class="link"
                >
                  <a
                    class="btn black"
                    :href="slide.link.url"
                    :target="slide.link.target"
                  >
                    {{ slide.link.title }}
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="slide.acf_fc_layout === 'banner_like'"
            class="slide banner-like"
          >
            <div
              v-if="(slide.bg_overlay > 0)"
              :style="{ opacity: slide.bg_overlay / 100 }"
              class="overlay"
            />

            <img
              :src="slide.bg_img.url"
              alt=""
              :fetchpriority="i === 0 ? 'high' : 'low'"
              decoding="async"
              class="bg"
              :style="{ objectPosition: `${slide.bg_img_x_pos}% ${slide.bg_img_y_pos}%` }"
            >

            <div class="container">
              <div class="orange" />
              <div
                class="header"
                v-html="stripBlockTags(slide.header)"
              />

              <div class="img">
                <img
                  :src="slide.bg_img.url"
                  :alt="slide.bg_img.alt"
                  :style="{ objectPosition: `${slide.bg_img_x_pos}% ${slide.bg_img_y_pos}%` }"
                >
              </div>

              <div
                class="content"
                v-html="slide.content"
              />

              <div
                v-if="slide.link"
                class="link"
              >
                <NuxtLink
                  class="btn black"
                  :to="slide.link.url"
                  :target="slide.link.target"
                >
                  {{ slide.link.title }}
                </NuxtLink>
              </div>
            </div>
          </div>
        </div>
      </VSlickCarousel>
    </section>

    <!-- DYNAMIC SECTIONS -->
    <RysDynamicSections
      class="dynamic-sections"
      :sections="page.acf?.sections || []"
    />

    <nuxt-link
      to="/contribute"
      class="contribute-float btn"
    >
      Share Your Story
    </nuxt-link>
  </main>
</template>

<script>
import { dom } from '@fortawesome/fontawesome-svg-core';

import stripBlockTags from '@/utils/strip-block-tags';
import yoastToVueMeta from '@/utils/yoast-to-vue-meta';

// Max time to keep the hero hidden while waiting on images/fonts
const CAROUSEL_READY_TIMEOUT = 2500;

function getSlideImageUrls(slide) {
  if (!slide)
    return [];

  if (slide.acf_fc_layout === 'banner_like')
    return [slide.bg_img?.url].filter(Boolean);

  return [slide.img?.url, slide.floating_image?.url].filter(Boolean);
}

function loadImage(url) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => (img.decode ? img.decode().catch(() => {}) : Promise.resolve()).then(resolve);
    img.onerror = resolve;
    img.src = url;
  });
}

export default {
  name: 'RysHome',

  async setup() {
    const wpJson = useWp();

    // Preload the first slide's images so the hero can render sooner.
    // Registered before the await so Nuxt's head context is still available.
    const firstSlideImages = ref([]);
    useHead({
      link: computed(() => firstSlideImages.value.map((href) => ({
        rel: 'preload',
        as: 'image',
        href,
        fetchpriority: 'high'
      })))
    });

    const { data: page, error: pageError } = await useAsyncData(
      'page-new-home',
      () => wpJson.pages.getBySlug('new-home')
    );

    if (pageError.value) {
      throw createError({ status: 500, cause: pageError.value });
    }

    if (!page.value) {
      throw createError({ statusCode: 404 });
    }

    firstSlideImages.value = getSlideImageUrls(page?.acf?.slides?.[0]);

    return { page };
  },

  data() {
    return {
      slideIndex: 0,
      isCarouselReady: false
    };
  },

  head() {
    return yoastToVueMeta(this.page);
  },

  computed: {
    carouselColorClass() {
      const slide = this.page?.acf?.slides?.[this.slideIndex];

      if (slide) {
        if (slide.acf_fc_layout === 'split_image' && slide.style === 'basic') {
          return 'orange';
        }

        return 'white';
      }

      return 'white';
    }
  },

  mounted() {
    dom.i2svg();

    this.revealCarouselWhenReady();

    // Bind tab visibility and window focus listeners for iOS mobile freeze recovery
    window.addEventListener('focus', this.refreshCarousel);
    document.addEventListener('visibilitychange', this.handleVisibility);

    this.$nextTick(() => {
      setTimeout(() => this.triggerActiveSlideAnimation(), 100);

      // Watch for mobile viewport changes
      this.resizeObserver = new ResizeObserver(() => {
        this.$nextTick(() => this.triggerActiveSlideAnimation());
      });
      this.resizeObserver.observe(document.querySelector('#hero'));
    });
  },

  beforeUnmount() {
    // Clean up visibility listeners
    window.removeEventListener('focus', this.refreshCarousel);
    document.removeEventListener('visibilitychange', this.handleVisibility);

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  },

  methods: {
    stripBlockTags,

    async revealCarouselWhenReady() {
      // Wait for the first slide's images and web fonts so the whole hero shows at once
      const assetsReady = Promise.all([
        ...getSlideImageUrls(this.page?.acf?.slides?.[0]).map(loadImage),
        document.fonts?.ready ?? Promise.resolve()
      ]);
      const timeout = new Promise(resolve => setTimeout(resolve, CAROUSEL_READY_TIMEOUT));

      await Promise.race([assetsReady, timeout]);

      this.refreshCarousel();
      requestAnimationFrame(() => {
        this.isCarouselReady = true;
      });
    },

    onImageLoad() {
      this.refreshCarousel();
    },

    handleVisibility() {
      if (document.visibilityState === 'visible') {
        this.refreshCarousel();
      }
    },

    refreshCarousel() {
      if (this.$refs.heroCarousel) {
        this.$nextTick(() => {
          // Force Slick to recalculate structural positions and widths
          if (typeof this.$refs.heroCarousel.createCarousel === 'function') {
            this.$refs.heroCarousel.createCarousel();
          }
          else if (typeof this.$refs.heroCarousel.setPosition === 'function') {
            this.$refs.heroCarousel.setPosition();
          }

          // Re-trigger text animations since structural heights might change
          setTimeout(() => this.triggerActiveSlideAnimation(), 150);
        });
      }
    },

    onCarouselBeforeChange() {
      const allElements = document.querySelectorAll('#hero .style-arrow-no .header, #hero .style-arrow-no .content, #hero .style-arrow-no .link');
      allElements.forEach((el) => {
        el.classList.remove('animate-in');
        el.style.removeProperty('animation');
        el.style.removeProperty('animation-delay');
      });
    },

    onCarouselAfterChange(newIndex) {
      this.slideIndex = newIndex;
      requestAnimationFrame(() => {
        setTimeout(() => this.triggerActiveSlideAnimation(), 400); // Back to 400ms
      });
    },

    triggerActiveSlideAnimation() {
      // Clear all animations first (classes + inline styles)
      const allElements = document.querySelectorAll('#hero .style-arrow-no .header, #hero .style-arrow-no .content, #hero .style-arrow-no .link');
      allElements.forEach((el) => {
        el.classList.remove('animate-in');
        el.style.removeProperty('animation');
        el.style.removeProperty('animation-delay');
      });

      requestAnimationFrame(() => {
        setTimeout(() => {
          const activeSlide = document.querySelector('#hero .v-slick-slide-group.active');
          if (!activeSlide)
            return;

          const elements = activeSlide.querySelectorAll('.style-arrow-no .header, .style-arrow-no .content, .style-arrow-no .link');
          if (elements.length === 0)
            return;

          elements.forEach((el, index) => {
            el.classList.add('animate-in');
          });
        }, 50);
      });
    }
  }
};
</script>

<style lang="scss" scoped>
@use 'sass:math';
@import '@/assets/css/utilities';

$slide-height: 550px;

.home-page {
  position: relative;
  overflow: hidden;
}

#hero {
  position: relative;
  z-index: 2;
  padding-bottom: 0;
  background-color: $thd-orange;

  .carousel {
    transition: opacity 300ms ease;
  }

  // Hide the carousel until slick has sized it and the first slide's assets are loaded,
  // reserving its space so the page below doesn't jump
  &.is-loading {
    min-height: 600px;
    overflow: hidden;

    @media screen and (min-width: $breakpoint-lg) {
      min-height: $slide-height;
    }

    .carousel {
      opacity: 0;
      visibility: hidden;
    }
  }
}

.dynamic-sections {
  position: relative;
  z-index: 1;
}

.carousel::v-deep {
  .v-slick-list {
    overflow: clip visible;

    .v-slick-track {
      position: absolute;
    }
  }

  .v-slick-dots {
    bottom: 344px;
    width: auto;
    left: 50%;
    transform: translate(-50%, 0%);

    @media screen and (min-width: $breakpoint-lg) {
      bottom: 30px;
      width: 1254px;
      text-align: left;
    }

    li button::before {
      content: '◻';
      color: $thd-white;
      font-size: 28px;
      // stylelint-disable-next-line alpha-value-notation
      opacity: 1;
      transition: color 500ms;
    }

    li.active button::before {
      content: '◼';
    }
  }

  &.orange {
    .v-slick-dots li button::before {
      color: $thd-orange;
    }
  }
}

.slide {
  height: auto;
  overflow: clip visible;

  @media screen and (min-width: $breakpoint-lg) {
    height: $slide-height;
  }

  img[alt='HBCU Tailgate Tour logo with football and goalposts'] {
    object-fit: contain !important;
  }

  &.split-image {
    position: unset;
    display: flex;
    flex-direction: column-reverse;
    align-items: flex-start;

    @media screen and (min-width: $breakpoint-lg) {
      position: relative;
      flex-direction: row;
      align-items: flex-end;
    }

    .bg {
      position: relative;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 1;

      @media screen and (min-width: $breakpoint-lg) {
        position: absolute;
      }

      .maybe-container {
        position: relative;
        height: 100%;
        display: flex;
        justify-content: flex-start;
        overflow-y: visible;

        .orange {
          display: none;
        }
      }
    }

    .container {
      position: relative;
      margin-bottom: 80px;
      margin-top: 32px;
      z-index: 2;
      display: flex;
      justify-content: flex-start;

      @media screen and (min-width: $breakpoint-lg) {
        margin-top: 0;
        margin-bottom: 80px;
      }

      > div {
        width: 100%;
        display: flex;
        flex-direction: column;

        @media screen and (min-width: $breakpoint-lg) {
          width: 45%;
        }
      }

      .header::v-deep {
        color: $thd-black;
        font-size: 20px;
        line-height: 24px;
        font-weight: 700;
        text-transform: capitalize;

        @media screen and (min-width: $breakpoint-lg) {
          font-stretch: condensed;
          font-size: 32px;
          line-height: 40px;
        }

        h1 {
          font-family: 'Yearbook Solid W01', sans-serif;
          font-size: 48px;
          font-weight: 400;
          line-height: 1;
          margin: 0;

          @media screen and (min-width: $breakpoint-lg) {
            font-size: 60px;
          }
        }

        h2 {
          margin: 5px 0;
          line-height: 1;
        }

        p {
          margin: 0;
        }

        strong,
        b {
          display: block;
          font-size: 72px;
          line-height: 65px;
          text-transform: uppercase;
          font-stretch: condensed;
          font-weight: 700;
          position: relative;

          @media screen and (min-width: $breakpoint-lg) {
            font-size: 96px;
            line-height: 96px;
          }
        }
      }

      .img {
        margin-top: 24px;
        max-width: 100%;

        img {
          max-width: 100%;
        }

        .aspect {
          position: relative;
          width: 100%;
          padding-top: 100%;

          @media screen and (width >= 430px) {
            padding-top: 70%;
          }

          @media screen and (min-width: $breakpoint-lg) {
            padding-top: math.div(9, 16) * 100%;
          }

          > img {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            object-fit: cover;
          }
        }

        @media screen and (min-width: $breakpoint-lg) {
          display: none;
        }
      }

      .content {
        color: $thd-black;
        font-size: 18px;
        font-weight: 400;
        line-height: 24px;

        @media screen and (min-width: $breakpoint-lg) {
          font-size: 28px;
          line-height: 36px;
          letter-spacing: -0.03em;
        }
      }

      .link {
        margin-top: 24px;
      }
    }

    &.img-left {
      .bg {
        .maybe-container {
          justify-content: flex-end;

          img {
            left: 0;
          }
        }
      }

      .container {
        justify-content: flex-end;
      }
    }

    &.style-basic {
      .bg {
        .maybe-container {
          @include container;

          .img {
            height: 100%;
            padding-top: 0;
            position: absolute;
            right: 0;
            width: 50%;
            z-index: 1;
            display: none;
            align-items: center;

            @media screen and (min-width: $breakpoint-lg) {
              display: flex;
            }

            .repeat {
              font-family: 'Yearbook Solid W01', sans-serif;
              font-size: 70px;
              font-weight: 400;
              line-height: 80px;
              color: #ffffff66;
            }

            .aspect {
              position: relative;
              width: 100%;
              padding-top: math.div(9, 16) * 100%;

              @media screen and (min-width: $breakpoint-lg) {
                padding-top: 100%;
              }

              > img {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                object-fit: cover;
              }
            }
          }
        }
      }

      &.img-left {
        .bg .maybe-container .img {
          right: unset;
          left: 0;
        }
      }
    }

    &.style-fancy {
      .bg {
        .maybe-container {
          .img {
            height: 100%;
            padding-top: 0;
            position: absolute;
            right: 0;
            width: 50%;
            z-index: 1;

            img {
              position: absolute;
              top: 0;
              width: 100%;
              height: 100%;
              object-fit: cover;
              object-position: center;
            }
          }

          .orange {
            display: block;
            position: relative;
            background-color: $thd-orange;
            width: 100%;
            transform: rotateZ(2deg) scale(110%);
            z-index: 2;

            @media screen and (min-width: $breakpoint-lg) {
              width: 50%;
              transform: rotateZ(2deg) scale(105%);
            }
          }
        }
      }

      .container {
        .header ::v-deep {
          color: $thd-white;
        }

        .content {
          color: $thd-white;
        }
      }

      &.img-left {
        .bg .maybe-container {
          .img {
            right: unset;
            left: 0;
          }

          .orange {
            transform: rotateZ(-2deg) scale(110%);

            @media screen and (min-width: $breakpoint-lg) {
              transform: rotateZ(-2deg) scale(105%);
            }
          }
        }
      }
    }

    &.style-arrow {
      $arrow-length: 200px;

      @media screen and (min-width: $breakpoint-lg) {
        align-items: center;
      }

      .bg {
        .maybe-container {
          .img {
            height: 100%;
            padding-top: 0;
            position: absolute;
            right: 0;
            width: calc(50% - math.div($slide-height, 7));
            background: #040404;
            z-index: 1;

            img {
              position: absolute;
              top: 0;
              width: 100%;
              height: 100%;
              object-fit: cover;
              object-position: center;
              z-index: 1;
            }

            @media screen and (min-width: $breakpoint-lg) {
              &::before,
              &::after {
                content: '';
                position: absolute;
                right: 0;
                width: 0;
                height: 0;
                border-left: math.div($slide-height, 2) solid transparent;
                z-index: 2;
              }

              &::before {
                top: 0;
                border-top: math.div($slide-height, 2) solid $thd-orange;
              }

              &::after {
                bottom: 0;
                border-bottom: math.div($slide-height, 2) solid $thd-orange;
              }
            }
          }

          .orange {
            display: block;
            position: relative;
            background-color: $thd-orange;
            width: 100%;
            z-index: 2;

            @media screen and (min-width: $breakpoint-lg) {
              width: calc(50% + math.div($slide-height, 7));

              &::after {
                content: '';
                position: absolute;
                top: 0;
                left: 100%;
                width: 0;
                height: 0;
                border-top: math.div($slide-height, 2) solid transparent;
                border-bottom: math.div($slide-height, 2) solid transparent;
                border-left: math.div($slide-height, 2) solid $thd-orange;
              }
            }
          }
        }
      }

      .container {
        margin-top: 0;

        .container-inner {
          position: relative;
          display: flex;
          flex-direction: column;

          @media screen and (min-width: $breakpoint-lg) {
            &::before,
            &::after {
              display: none;
            }
          }

          > *:last-child {
            margin-bottom: 0;
          }
        }

        .header ::v-deep {
          color: $thd-white;
          font-size: 1.8em;
        }

        .img {
          position: relative;
          width: 100vw;
          max-width: 100vw;
          left: calc((100vw - 100%) / -2);
          clip-path: polygon(0 0, 65% 0, 100% 50%, 65% 100%, 0 100%);
          background-color: $thd-white;
          order: -1;
          margin-top: 0;
          margin-bottom: 24px;

          .aspect {
            img {
              object-fit: cover;
            }
          }
        }

        .content {
          color: $thd-white;
        }

        @media screen and (min-width: $breakpoint-lg) {
          margin-bottom: 0;
        }
      }

      &.img-left {
        .bg .maybe-container {
          .img {
            right: unset;
            left: 0;

            @media screen and (min-width: $breakpoint-lg) {
              &::before,
              &::after {
                left: 0;
                right: unset;
                border-left: 0;
                border-right: $arrow-length solid transparent;
              }
            }
          }

          .orange {
            @media screen and (min-width: $breakpoint-lg) {
              &::after {
                left: unset;
                right: 100%;
                border-left: 0;
                border-right: $arrow-length solid $thd-orange;
              }
            }
          }
        }
      }
    }

    &.style-arrow-simple {
      .bg .maybe-container {
        .orange {
          background-color: $thd-white;

          @media (min-width: $breakpoint-lg) {
            background-color: $thd-orange;
            width: 70%;
          }
        }

        .img {
          &::before,
          &::after {
            display: none;
          }
        }
      }

      .container .container-inner {
        @media (min-width: $breakpoint-lg) {
          width: 75%;
        }

        .header::v-deep {
          pointer-events: none;
          position: absolute;
          width: 85%;
          height: 100vw;
          text-transform: uppercase;
          display: flex;
          flex-direction: column;
          justify-content: center;
          z-index: 1;

          @media (width >= 430px) {
            height: 70vw;
          }

          @media (min-width: $breakpoint-lg) {
            position: static;
            display: block;
            font-size: 64px;
            line-height: 60px;
            width: auto;
            height: auto;
          }

          b,
          strong {
            font-size: 100px;
            line-height: 100px;
            display: inline;

            @media (min-width: $breakpoint-lg) {
              font-size: 256px;
              line-height: 220px;
            }
          }

          .num {
            position: relative;
            float: left;
            font-size: 180px;
            line-height: 150px;
            top: -0.1em;

            @media (min-width: $breakpoint-lg) {
              font-size: 350px;
              line-height: 280px;
              top: -0.06em;
            }
          }

          .num ~ strong {
            @media (max-width: $breakpoint-lg) {
              font-size: 2.5em;
              line-height: 1.2em;
            }
          }
        }

        .img {
          background-color: $thd-orange;
        }

        .link {
          margin-top: 16px;
        }
      }
    }

    &.style-arrow-no {
      $arrow-length: 200px;

      @media screen and (min-width: $breakpoint-lg) {
        align-items: center;
      }

      .bg {
        .maybe-container {
          .img {
            height: 320px;
            padding-top: 0;
            position: relative;
            top: 100%;
            right: 0;
            left: 0;
            width: 100%;
            background: #040404;
            overflow: hidden;
            z-index: 2;

            @media screen and (min-width: $breakpoint-lg) {
              position: absolute;
            }

            .aspect {
              height: 100%;

              &:has(.float-image) {
                height: 0;
              }
            }

            img {
              position: relative;
              top: 0;
              width: 100%;
              height: 100%;
              object-fit: cover;
              object-position: center;
              z-index: 1;

              @media screen and (min-width: $breakpoint-lg) {
                position: absolute;
              }

              &.float-image {
                position: absolute;
                height: 100%;
                right: 0;
              }
            }

            .floating {
              position: absolute;
              bottom: 20px;
              right: 20px;
              color: #ffffff;
              background-color: #00000057;
              padding: 5px;
              z-index: 1;
            }

            @media screen and (min-width: $breakpoint-lg) {
              top: unset;
              left: unset;
              height: 100%;
              width: calc(50% - math.div($slide-height, 7));

              &::before,
              &::after {
                content: '';
                position: absolute;
                right: 0;
                width: 0;
                height: 0;
                border-left: 0 solid transparent;
                z-index: 2;
              }

              &::before {
                top: 0;
                border-top: math.div($slide-height, 2) solid $thd-orange;
              }

              &::after {
                bottom: 0;
                border-bottom: math.div($slide-height, 2) solid $thd-orange;
              }
            }
          }

          .orange {
            display: none;
            position: relative;
            background-color: $thd-orange;
            width: 100%;
            z-index: 2;

            @media screen and (min-width: $breakpoint-lg) {
              width: calc(50% + math.div($slide-height, 7));

              &::after {
                content: '';
                position: absolute;
                top: 0;
                left: 100%;
                width: 0;
                height: 0;
                border-top: 0 solid transparent;
                border-bottom: 0 solid transparent;
                border-left: 0 solid $thd-orange;
              }
            }
          }
        }
      }

      .container {
        text-align: center;
        margin-top: 60px;

        @media screen and (min-width: $breakpoint-lg) {
          text-align: left;
          margin-top: 0;
        }

        .container-inner {
          position: relative;
          display: flex;
          flex-direction: column;

          @media screen and (min-width: $breakpoint-lg) {
            width: 55%;
          }
        }

        .header,
        .content,
        .link {
          opacity: 0;
          transform: translateX(-100%);
          visibility: hidden;

          @media (max-width: $breakpoint-lg) {
            will-change: transform, opacity;
            backface-visibility: hidden;
            transform: translate3d(-100%, 0, 0);
          }

          &.animate-in {
            animation-fill-mode: both;

            @media (max-width: $breakpoint-lg) {
              animation-duration: 0.9s; // Slightly longer for mobile
            }
          }
        }

        .header ::v-deep {
          color: $thd-white;
          font-size: 1.8em;
        }

        .img {
          display: none;
          position: relative;
          width: 100vw;
          max-width: 100vw;
          left: calc((100vw - 100%) / -2);
          clip-path: polygon(0 0, 100% 0, 100% 100%, 65% 100%, 0 100%);
          background-color: $thd-orange;
          order: 1;
          margin-top: 100px;
          margin-bottom: 24px;

          .aspect {
            img {
              object-fit: cover;
            }
          }
        }

        .content {
          color: $thd-white;
        }

        .link {
          // opacity: 0;
          // transform: translateX(-100%);
          // visibility: hidden;
          // animation: slide-in-left 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.6s forwards;
        }

        @media screen and (min-width: $breakpoint-lg) {
          margin-bottom: 0;
          max-width: 1240px;
        }
      }

      @keyframes slide-in-left {
        from {
          opacity: 0;
          transform: translate3d(-100%, 0, 0); // Use 3D transforms for mobile
          visibility: hidden;
        }

        to {
          opacity: 1;
          transform: translate3d(0, 0, 0);
          visibility: visible;
        }
      }

      .header.animate-in,
      .content.animate-in,
      .link.animate-in {
        animation: slide-in-left 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
      }

      // Desktop overrides
      @media (min-width: $breakpoint-lg) {
        .header.animate-in {
          animation-delay: 0.2s;
        }

        .content.animate-in,
        .link.animate-in {
          animation-delay: 0.6s;
        }
      }

      &.img-left {
        // ... rest unchanged
      }
    }
  }

  &.banner-like {
    position: relative;
    display: flex;
    background-size: cover;

    @media screen and (min-width: $breakpoint-lg) {
      align-items: flex-end;
    }

    > img {
      position: absolute;
      z-index: 1;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;

      &.bg {
        display: none;

        @media screen and (min-width: $breakpoint-lg) {
          display: block;
        }
      }
    }

    .overlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: #000000;
      z-index: 2;
    }

    .container {
      position: relative;
      z-index: 2;
      margin-bottom: 80px;

      @media screen and (min-width: $breakpoint-lg) {
        margin-bottom: 120px;
      }

      .orange {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: -1;
        background-color: $thd-orange;
        transform: rotateZ(2deg) scale(125%);

        @media screen and (min-width: $breakpoint-lg) {
          display: none;
        }
      }

      .header ::v-deep {
        color: $thd-white;
        font-size: 20px;
        font-weight: 700;
        line-height: 24px;
        text-transform: capitalize;
        margin-top: 32px;

        @media screen and (min-width: $breakpoint-lg) {
          margin-top: 0;
          font-size: 40px;
          line-height: 50px;
          font-stretch: condensed;
          text-transform: capitalize;
        }

        strong,
        b {
          display: block;
          font-size: 72px;
          line-height: 65px;
          text-transform: uppercase;
          font-stretch: condensed;
          font-weight: 700;
          position: relative;
          left: -2px;

          @media screen and (min-width: $breakpoint-lg) {
            font-size: 96px;
            line-height: 96px;
            margin-top: -20px;
          }
        }
      }

      .content {
        color: $thd-white;
        font-size: 18px;
        font-weight: 400;
        line-height: 24px;

        @media screen and (min-width: $breakpoint-lg) {
          font-size: 24px;
          line-height: 36px;
          letter-spacing: -0.03em;
        }
      }

      .link {
        .btn {
          border: 3px solid transparent;

          @media screen and (min-width: $breakpoint-lg) {
            background: $thd-orange;
            color: $thd-white;

            &:hover {
              background: $thd-white;
              color: $thd-orange;
            }
          }
        }
      }

      .img {
        margin-top: 24px;
        max-width: 100%;

        img {
          max-width: 100%;
        }

        .aspect {
          position: relative;
          width: 100%;
          padding-top: 100%;

          @media screen and (width >= 430px) {
            padding-top: 70%;
          }

          @media screen and (min-width: $breakpoint-lg) {
            padding-top: math.div(9, 16) * 100%;
          }

          > img {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            object-fit: cover;
          }
        }

        @media screen and (min-width: $breakpoint-lg) {
          display: none;
        }
      }
    }
  }
}

.repeat {
  font-family: 'Yearbook Solid W01', sans-serif;
  font-size: 33px;
  font-weight: 400;
  line-height: 45px;
  color: #ffffff66;
  list-style-type: none;
  padding: 0 0 0 15px;
  margin: 0;
  position: relative;

  @media screen and (min-width: $breakpoint-lg) {
    font-size: 55px;
    line-height: 78px;
    white-space: nowrap;
  }
}

.repeat {
  li {
    animation: highlight 3.5s infinite ease-in-out;

    &:nth-child(1) {
      animation-delay: 0s;
    }

    &:nth-child(2) {
      animation-delay: 0.5s;
    }

    &:nth-child(3) {
      animation-delay: 1s;
    }

    &:nth-child(4) {
      animation-delay: 1.5s;
    }

    &:nth-child(5) {
      animation-delay: 2s;
    }

    &:nth-child(6) {
      animation-delay: 2.5s;
    }

    &:nth-child(7) {
      animation-delay: 3s;
    }
  }
}

@keyframes highlight {
  40%,
  60% {
    color: #ffffff66;
  }

  50% {
    color: #f96302;
  }
}

.contribute-float {
  display: flex;
  align-items: center;
  justify-content: center;
  position: fixed;
  bottom: 40px;
  right: 40px;
  width: 88px;
  height: 88px;
  font-size: 16px;
  line-height: 1;
  border: 2px solid #ffffff;
  border-radius: 200px;
  z-index: 1;

  @media screen and (max-width: $breakpoint-lg) {
    display: none;
  }
}
</style>
